import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock } from 'lucide-react';
import AuthShell from '../../components/auth/AuthShell';
import Loader2 from '@/components/brand/Loader2';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // F1: motivo por el que se cerró la sesión anterior (otro equipo, caducidad, admin).
  useEffect(() => {
    try {
      const reason = sessionStorage.getItem('onor_logout_reason');
      if (reason) {
        sessionStorage.removeItem('onor_logout_reason');
        toast.warning(reason, { duration: 8000 });
      }
    } catch (e) { /* sin sessionStorage */ }
  }, []);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { user, status } = await login(formData.email, formData.password);
      toast.success('¡Bienvenido de vuelta!');
      
      // Redirect based on user status from API
      const routing = status?.routing || {};
      
      // Check roles first
      if (user.roles?.includes('admin') || user.roles?.includes('super_admin')) {
        navigate('/admin/dashboard');
      } else if (user.roles?.includes('coach')) {
        navigate('/coach/dashboard');
      } 
      // Then check routing status
      else if (routing.needs_onboarding) {
        navigate('/onboarding');
      } else if (routing.needs_assessment) {
        navigate('/assessment');
      } else if (routing.needs_approval) {
        navigate('/pending-approval');
      } else if (routing.can_access_app) {
        navigate('/app/dashboard');
      } else {
        // Fallback to onboarding if status unclear
        navigate('/onboarding');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <div className="flex justify-center gap-3 font-display font-semibold uppercase tracking-[.2em] text-xs text-zinc-400 onor-rise">
        <span>Honor</span><span>·</span><span>Disciplina</span><span>·</span><span className="text-bronze">Fuerza</span>
      </div>
      <h1 className="font-display font-bold uppercase text-3xl leading-9 text-center text-zinc-100 onor-rise" style={{ animationDelay: '.08s' }}>
        Bienvenido de vuelta
        <small className="block text-sm leading-5 font-medium tracking-wider normal-case text-zinc-400 mt-1">Entra y sigue donde lo dejaste.</small>
      </h1>

      <form onSubmit={handleSubmit} className="space-y-4" noValidate={false}>
        <div className="space-y-1 onor-rise" style={{ animationDelay: '.16s' }}>
          <Label htmlFor="email" className="font-display font-semibold uppercase tracking-[.14em] text-xs text-zinc-400">Email</Label>
          <div className="flex items-center gap-2 bg-zinc-950 border border-zinc-700 rounded-sm px-3 min-h-[48px] focus-within:border-primary transition-colors">
            <Mail className="w-5 h-5 text-zinc-400 shrink-0" aria-hidden="true" />
            <Input
              id="email"
              type="email"
              inputMode="email"
              autoComplete="username"
              placeholder="tu@email.com"
              className="flex-1 min-w-0 bg-transparent border-0 shadow-none focus-visible:ring-0 px-0 text-base text-zinc-100 placeholder:text-zinc-600"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              data-testid="login-email-input"
            />
          </div>
        </div>

        <div className="space-y-1 onor-rise" style={{ animationDelay: '.24s' }}>
          <Label htmlFor="password" className="font-display font-semibold uppercase tracking-[.14em] text-xs text-zinc-400">Contraseña</Label>
          <div className="flex items-center gap-2 bg-zinc-950 border border-zinc-700 rounded-sm px-3 min-h-[48px] focus-within:border-primary transition-colors">
            <Lock className="w-5 h-5 text-zinc-400 shrink-0" aria-hidden="true" />
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••"
              className="flex-1 min-w-0 bg-transparent border-0 shadow-none focus-visible:ring-0 px-0 text-base text-zinc-100 placeholder:text-zinc-600"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              data-testid="login-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="min-w-[44px] min-h-[44px] -mr-2 inline-flex items-center justify-center text-zinc-400 hover:text-zinc-100"
              aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <div className="flex justify-end onor-rise" style={{ animationDelay: '.24s' }}>
          <Link
            to="/forgot-password"
            className="text-sm text-zinc-400 hover:text-zinc-100 underline underline-offset-4 min-h-[44px] inline-flex items-center"
            data-testid="forgot-password-link"
          >
            ¿Olvidaste tu contraseña?
          </Link>
        </div>

        <Button
          type="submit"
          className="btn-primary w-full onor-rise"
          style={{ animationDelay: '.32s' }}
          disabled={loading}
          data-testid="login-submit-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Ingresando…
            </>
          ) : (
            '[Iniciar sesión]'
          )}
        </Button>
      </form>

      <div className="flex items-center gap-3 text-zinc-400 font-display text-xs tracking-[.14em] uppercase onor-rise" style={{ animationDelay: '.32s' }}>
        <span className="flex-1 h-px bg-zinc-800" /><span>¿Nuevo en ONOR?</span><span className="flex-1 h-px bg-zinc-800" />
      </div>
      <Link to="/register" className="btn-secondary w-full onor-rise" style={{ animationDelay: '.4s' }} data-testid="login-register-link">
        Crear cuenta gratis
      </Link>
      <p className="text-center text-xs text-zinc-400 onor-rise" style={{ animationDelay: '.4s' }}>Sin tarjeta · Se instala en iOS y Android</p>
    </AuthShell>
  );
};

export default Login;
